import { useState } from "react";
import { styled } from "@pigment-css/react";
import { ChevronGrabberVertical, TrashCanSimple } from "@acto/web-common/components";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { RecurrenceCadence } from "@acto/gql/graphql";
import { useDeleteMeeting } from "@acto/taskpane/core/data-access/meetings/useDeleteMeeting";
import { useEndRecurringMeetingFrom } from "@acto/taskpane/core/data-access/meetings/useEndRecurringMeetingFrom";
import { useRescheduleOccurrence } from "@acto/taskpane/core/data-access/meetings/useRescheduleOccurrence";
import { useSkipOccurrence } from "@acto/taskpane/core/data-access/meetings/useSkipOccurrence";
import { useUpdateMeeting } from "@acto/taskpane/core/data-access/meetings/useUpdateMeeting";
import { useUpdateRecurringMeetingFrom } from "@acto/taskpane/core/data-access/meetings/useUpdateRecurringMeetingFrom";
import { FullScreenSheet, SheetHeaderButton } from "@acto/taskpane/field-sales-2/components/primitives/BottomSheet";
import { MenuPopover } from "@acto/taskpane/field-sales-2/components/primitives/MenuPopover";
import { todayUtc, toIsoDate } from "@acto/taskpane/field-sales-2/components/primitives/Calendar";
import { Collapse } from "@acto/taskpane/field-sales-2/components/primitives/Collapse";
import { DatePicker } from "@acto/taskpane/field-sales-2/components/primitives/DatePicker";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";
import { CtaRow, SolidCtaButton } from "@acto/taskpane/field-sales-2/components/primitives/SheetCta";
import {
  defaultMeetingTimes,
  plusOneHour,
  RepeatField,
  TimeRange,
} from "@acto/taskpane/field-sales-2/components/patterns/MeetingForm";
import { pressInteraction } from "@acto/taskpane/field-sales-2/styles/pressInteraction";
import { formatNumericDate } from "@acto/taskpane/field-sales-2/utils/format";
import { ScopeSheet, type MeetingScope } from "./ScopeSheet";

const Card = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  marginInline: theme.fieldSalesTwo.spacing[12],
  padding: `${theme.fieldSalesTwo.spacing[24]} ${theme.fieldSalesTwo.spacing[20]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: theme.fieldSalesTwo.colors.surface.glass,
}));

// One Card slot for the date field + calendar Collapse, so the closed (zero-height) Collapse adds no gap.
const DateSection = styled("div")({
  display: "flex",
  flexDirection: "column",
  width: "100%",
});

const DateField = styled("button")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: theme.fieldSalesTwo.spacing[8],
  width: "100%",
  height: theme.fieldSalesTwo.spacing[48],
  paddingInline: theme.fieldSalesTwo.spacing[16],
  boxSizing: "border-box",
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.neutral[300]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  background: "transparent",
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size.base,
  fontWeight: theme.fieldSalesTwo.font.weight[500],
  color: theme.fieldSalesTwo.colors.neutral[900],
  cursor: "pointer",
  WebkitTapHighlightColor: "transparent",
  ...pressInteraction(theme, { scale: 1 }),
}));

const DateValue = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[8],
  "& svg": { width: theme.fieldSalesTwo.spacing[18], height: theme.fieldSalesTwo.spacing[18] },
}));

const CalendarPad = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.fieldSalesTwo.spacing[24],
  paddingBottom: theme.fieldSalesTwo.spacing[24],
}));

const SaveButton = styled(SolidCtaButton)(({ theme }) => ({
  background: theme.fieldSalesTwo.colors.charts.primary,
}));

const DeleteToggle = styled(SheetHeaderButton)(({ theme }) => ({
  background: theme.fieldSalesTwo.colors.status.error.tint,
  color: theme.fieldSalesTwo.colors.status.error.main,
  borderColor: "transparent",
}));

interface EditableBase {
  date: string;
  startTime?: string | null;
  endTime?: string | null;
}

export type EditableMeeting =
  | (EditableBase & { kind: "standalone"; meetingId: string })
  | (EditableBase & {
      kind: "series";
      seriesId: string;
      /** Series anchor of the edited occurrence (immutable even after a reschedule). */
      occurrenceDate: string;
      cadence: RecurrenceCadence;
    });

type PendingScope =
  | { action: "delete" }
  | { action: "save"; scheduledDate: string; times: { from: string; to: string }; cadence: RecurrenceCadence };

interface EditMeetingSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  meeting: EditableMeeting;
  /** Fired after a successful delete, with the deleted meeting's scheduled ISO date (for a confirmation). */
  onDeleted?: (deletedDate: string) => void;
}

export function EditMeetingSheet({ isOpen, onOpenChange, meeting, onDeleted }: EditMeetingSheetProps) {
  const t = useTranslations("fieldSalesTwo.editMeeting");
  const tActions = useTranslations("fieldSalesTwo.customerActions");
  const { locale } = useLocale();

  const { updateMeeting, result: updateResult } = useUpdateMeeting();
  const { deleteMeeting, result: deleteResult } = useDeleteMeeting();
  const { rescheduleOccurrence, result: rescheduleResult } = useRescheduleOccurrence();
  const { skipOccurrence, result: skipResult } = useSkipOccurrence();
  const { updateRecurringMeetingFrom, result: updateFromResult } = useUpdateRecurringMeetingFrom();
  const { endRecurringMeetingFrom, result: endFromResult } = useEndRecurringMeetingFrom();

  const [date, setDate] = useState<Date>(() => new Date(meeting.date));
  const [times, setTimes] = useState(() =>
    meeting.startTime && meeting.endTime ? { from: meeting.startTime, to: meeting.endTime } : defaultMeetingTimes(),
  );
  const [cadence, setCadence] = useState<RecurrenceCadence>(
    meeting.kind === "series" ? meeting.cadence : RecurrenceCadence.Weekly,
  );
  const [calendarExpanded, setCalendarExpanded] = useState(false);
  const [pending, setPending] = useState<PendingScope | null>(null);

  const isSeries = meeting.kind === "series";
  // HH:mm strings are zero-padded, so a lexical compare is a valid time compare.
  const isValid = Boolean(times.from && times.to && times.to > times.from);
  const isMutating =
    updateResult.fetching ||
    deleteResult.fetching ||
    rescheduleResult.fetching ||
    skipResult.fetching ||
    updateFromResult.fetching ||
    endFromResult.fetching;

  const close = () => {
    setPending(null);
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!isValid) return;
    const scheduledDate = toIsoDate(date);

    if (meeting.kind === "series") {
      setPending({ action: "save", scheduledDate, times, cadence });
      return;
    }

    const updated = await updateMeeting({
      id: meeting.meetingId,
      scheduledDate,
      scheduledStartTime: times.from,
      scheduledEndTime: times.to,
    });
    if (updated) close();
  };

  const handleConfirmDelete = async () => {
    if (meeting.kind === "series") {
      setPending({ action: "delete" });
      return;
    }

    const deleted = await deleteMeeting(meeting.meetingId);
    if (deleted) {
      onDeleted?.(meeting.date);
      close();
    }
  };

  const handleApplyScope = async (scope: MeetingScope) => {
    if (!pending || meeting.kind !== "series") return;

    const applied =
      pending.action === "delete"
        ? scope === "this"
          ? await skipOccurrence(meeting.seriesId, meeting.occurrenceDate)
          : await endRecurringMeetingFrom(meeting.seriesId, meeting.occurrenceDate)
        : scope === "this"
          ? await rescheduleOccurrence({
              seriesId: meeting.seriesId,
              occurrenceDate: meeting.occurrenceDate,
              scheduledDate: pending.scheduledDate,
              scheduledStartTime: pending.times.from,
              scheduledEndTime: pending.times.to,
            })
          : await updateRecurringMeetingFrom({
              seriesId: meeting.seriesId,
              fromOccurrenceDate: meeting.occurrenceDate,
              cadence: pending.cadence,
              startDate: pending.scheduledDate,
              scheduledStartTime: pending.times.from,
              scheduledEndTime: pending.times.to,
            });

    if (applied) {
      if (pending.action === "delete") onDeleted?.(meeting.date);
      close();
    }
  };

  return (
    <FullScreenSheet
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title={t("title")}
      closeAriaLabel={tActions("closeAriaLabel")}
      headerAction={
        <MenuPopover
          ariaLabel={t("delete")}
          trigger={
            <DeleteToggle type="button" aria-label={t("delete")} disabled={isMutating}>
              <TrashCanSimple />
            </DeleteToggle>
          }
          items={[
            { label: t("confirmDelete"), tone: "destructive", disabled: isMutating, onSelect: handleConfirmDelete },
          ]}
        />
      }
    >
      <Card>
        <DateSection>
          <Collapse open={calendarExpanded}>
            <CalendarPad>
              <DatePicker
                value={date}
                // A series occurrence never moves into the past; a one-off stays back-datable.
                minDate={isSeries ? todayUtc() : undefined}
                onChange={(next) => {
                  setDate(next);
                  setCalendarExpanded(false);
                }}
              />
              <Divider />
            </CalendarPad>
          </Collapse>
          <DateField
            type="button"
            aria-expanded={calendarExpanded}
            onClick={() => setCalendarExpanded((expanded) => !expanded)}
          >
            {t("date")}
            <DateValue>
              {formatNumericDate(toIsoDate(date), locale)}
              <ChevronGrabberVertical aria-hidden="true" />
            </DateValue>
          </DateField>
        </DateSection>
        <Divider />
        <TimeRange
          fromTime={times.from}
          toTime={times.to}
          onFromChange={(from) =>
            setTimes((current) => ({ from, to: current.to > from ? current.to : plusOneHour(from) }))
          }
          onToChange={(to) => setTimes((current) => ({ ...current, to }))}
        />
        <Divider />
        <RepeatField repeat={isSeries} cadence={cadence} onCadenceChange={setCadence} />
      </Card>
      <CtaRow>
        <SaveButton type="button" onClick={handleSave} disabled={isMutating || !isValid}>
          {t("saveChanges")}
        </SaveButton>
      </CtaRow>
      {pending && (
        <ScopeSheet
          key={pending.action}
          isOpen
          onOpenChange={(open) => {
            if (!open) setPending(null);
          }}
          title={pending.action === "delete" ? t("scopeDeleteTitle") : t("scopeSaveTitle")}
          thisDisabled={pending.action === "save" && isSeries && pending.cadence !== meeting.cadence}
          onApply={handleApplyScope}
          applying={isMutating}
        />
      )}
    </FullScreenSheet>
  );
}
